/* @flow */

import type { NETGEM_API_V8_HUB } from '../../../../libs/netgemLibrary/v8/types/Hub';
import type { AppConfigurationState } from '../../../appConf/reducers';
import type { CombinedReducers } from '../../../reducers';
import type { Dispatch } from '../../../types/types';
import type { NetgemApiEmitterType, RequestResponseMethodDefinitionType } from '../emitter';

import { MILLISECONDS_PER_SECOND } from '../../../../helpers/dateTime/Format';
import ContinuousWatching from '../../../../helpers/viewingHistory/ContinuousWatching';
import { createCustomNetworkErrorFromKey } from '../../../../libs/netgemLibrary/helpers/CreateCustomNetworkError';
import { type NETGEM_API_V8_AVENUE } from '../../../../libs/netgemLibrary/v8/types/Avenue';
import { FeedProviderKind } from '../../../../libs/netgemLibrary/v8/types/Feed';
import { type NETGEM_API_V8_REQUEST_RESPONSE } from '../../../../libs/netgemLibrary/v8/types/RequestResponse';
import { type NETGEM_API_V8_SECTION } from '../../../../libs/netgemLibrary/v8/types/Section';
import { type NETGEM_API_V8_DEFAULT_ACTIONS } from '../../../../libs/netgemLibrary/v8/types/WidgetConfig';
import { updateAvenueList, updateDefaultActions } from '../../../ui/actions';
import { REDUX_MSG_REQUEST_GENERIC } from '../../constants/';
import { generateApiUrl } from '../helpers/api';
import { getSettingValueByName } from '../helpers/settings';

const validateSection: (section: NETGEM_API_V8_SECTION) => boolean = (section) => {
  const { model } = section;
  const { provider } = model;

  if (provider === FeedProviderKind.Aggregation) {
    const { sources = [] } = model;

    if (!sources || sources.length === 0) {
      return true;
    }

    for (let i = 0; i < sources.length; i++) {
      const { provider: srcProvider } = sources[i];
      if (srcProvider !== FeedProviderKind.NtgVideo && srcProvider !== FeedProviderKind.Local) {
        return false;
      }
    }

    return true;
  }

  return provider === FeedProviderKind.NtgVideo || provider === FeedProviderKind.Local;
};

const validateAvenue: (avenue: NETGEM_API_V8_AVENUE) => boolean = (avenue) => {
  const { sections } = avenue;

  if (!sections) {
    return false;
  }

  avenue.sections = sections.filter(validateSection);
  return sections.length > 0;
};

const getValidHub: (hub: NETGEM_API_V8_HUB) => NETGEM_API_V8_HUB = (hub) => hub.filter(validateAvenue);

const sendV8HubRequest: (hubKey: ?string, signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (hubKey, signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers, NetgemApiEmitter: NetgemApiEmitterType): Promise<NETGEM_API_V8_HUB> => {
    const state = getState();
    const {
      appConfiguration,
      netgemApi: { hubUrl },
    } = state;

    if (!hubUrl) {
      return Promise.reject(createCustomNetworkErrorFromKey('common.messages.errors.missing_url_definition', { urlDef: 'hubUrl' }));
    }

    /*
     * The old 'platform.hubkey' has been replaced by 'platform.hubkeys.home' but the setting has yet to be deployed in all realms and environments
     * So, in the meantime, we fall back to the old setting
     */
    const key = hubKey ?? getSettingValueByName('platform.hubkeys', 'home', appConfiguration) ?? getSettingValueByName('platform', 'hubkey', appConfiguration);
    const { authent, method } = hubUrl;

    return NetgemApiEmitter.emit(REDUX_MSG_REQUEST_GENERIC, {
      authent,
      method,
      signal,
      uri: generateApiUrl(hubUrl, { key }, state),
    }).then((response: NETGEM_API_V8_REQUEST_RESPONSE) => {
      const { cacheMaxAge, result } = response;
      const hubResult: NETGEM_API_V8_HUB = getValidHub(result);

      if (!hubKey) {
        // General hub: sets all avenues

        const hubMaxAge = cacheMaxAge ? cacheMaxAge * MILLISECONDS_PER_SECOND : null;
        dispatch(updateAvenueList(hubResult, hubMaxAge));
      }

      return hubResult;
    });
  };

const isContinuousWatchingKeyDefault: (appConfiguration: AppConfigurationState) => boolean = (appConfiguration) => {
  const defaultKey = getSettingValueByName('platform', 'defaultkey', appConfiguration);
  const continuousWatchingKey = getSettingValueByName('platform', 'continuouswatchingkey', appConfiguration);

  return continuousWatchingKey === null || continuousWatchingKey === defaultKey;
};

const sendV8DefaultHubRequest: (signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers, NetgemApiEmitter: NetgemApiEmitterType): Promise<void> => {
    const state = getState();
    const {
      appConfiguration,
      netgemApi: { defaultHubUrl },
    } = state;

    if (!defaultHubUrl) {
      // Old version
      return Promise.resolve();
    }

    const { authent, method } = defaultHubUrl;
    return NetgemApiEmitter.emit(REDUX_MSG_REQUEST_GENERIC, {
      authent,
      method,
      signal,
      uri: generateApiUrl(defaultHubUrl, {}, state),
    }).then((response: NETGEM_API_V8_REQUEST_RESPONSE) => {
      const { actions } = response.result as NETGEM_API_V8_DEFAULT_ACTIONS;

      dispatch(updateDefaultActions(actions));

      if (isContinuousWatchingKeyDefault(appConfiguration)) {
        ContinuousWatching.initialize(actions, dispatch, getState);
      }
    });
  };

const sendV8ContinuousWatchingHubRequest: (signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers, NetgemApiEmitter: NetgemApiEmitterType): Promise<void> => {
    const state = getState();
    const {
      appConfiguration,
      netgemApi: { defaultHubUrl },
    } = state;

    if (!defaultHubUrl) {
      // Old version
      return Promise.resolve();
    }

    if (isContinuousWatchingKeyDefault(appConfiguration)) {
      // Continuous watching key not defined in DMS (or same as default key): will be defined in sendV8DefaultHubRequest
      return Promise.resolve();
    }

    const continuousWatchingKey = getSettingValueByName('platform', 'continuouswatchingkey', appConfiguration);

    // Continuous watching key is defined and different from default key
    const { authent, method } = defaultHubUrl;
    return NetgemApiEmitter.emit(REDUX_MSG_REQUEST_GENERIC, {
      authent,
      method,
      signal,
      uri: generateApiUrl(defaultHubUrl, { defaultKey: continuousWatchingKey }, state),
    }).then((response: NETGEM_API_V8_REQUEST_RESPONSE) => {
      const { actions } = response.result as NETGEM_API_V8_DEFAULT_ACTIONS;

      ContinuousWatching.initialize(actions, dispatch, getState);
    });
  };

export { getValidHub, sendV8ContinuousWatchingHubRequest, sendV8DefaultHubRequest, sendV8HubRequest };
